import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { usePostHog } from '@posthog/react'
import type { ReactElement } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import Sidebar from './components/Sidebar'
import Topbar from './components/Topbar'
import CommandPalette from './components/CommandPalette'
import './App.css'
import './style/Darkmode.css'
import './style/Mobile.css'
import HomeView from './views/HomeView'
import DashboardView from './views/DashboardView'
import SettingsView from './views/SettingsView'
import ContactsView from './views/ContactsView'
import CalendarView from './views/CalendarView'
import CrmSyncView from './views/CrmSyncView'
import SignupView from './views/SignupView'
import RedirectView from './views/RedirectView'
import ArchivesView from './views/ArchivesView'
import ContactCreateView from './views/ContactCreateView'
import ContactMergeView from './views/ContactMergeView'
import MemoriesView from './views/MemoriesView'
import CatalogUploaderView from './views/CatalogUploaderView'
import CatalogRawView from './views/CatalogRawView'
import AssistantView from './views/AssistantView'
import LoginView from './views/LoginView'
import PaymentsView from './views/PaymentsView'
import PaymentResultView from './views/PaymentResultView'
import FeatureFlagsView from './views/FeatureFlagsView'
import BugReportModal from './components/BugReportModal'
import ErrorBoundary from './components/ErrorBoundary'
import LoadingPlaceholder from './components/LoadingPlaceholder'
import OnboardingView, { ONBOARDING_STATUS_KEY } from './components/onboarding/OnboardingView'
import OnboardingSkipBanner from './components/onboarding/OnboardingSkipBanner'
import OnboardingCoachMark from './components/onboarding/OnboardingCoachMark'
const ActionItemsView = lazy(() => import('./views/ActionItemsView'))
const ContactDetailView = lazy(() => import('./views/ContactDetailView'))
const ConversationDetailView = lazy(() => import('./views/ConversationDetailView'))

type BillingCheckStatus = 'unknown' | 'checking' | 'active' | 'inactive'
import {
  getSessionAuth,
  getSessionToken,
  hasSessionMetadata,
  logoutSession,
  setSessionAuth,
} from './utils/sessionAuth'
import { refreshAccessToken } from './api/base'
import {
  clearSessionUserId,
  ensureSessionUserId,
  getSessionUserId,
  setSessionUserId as storeSessionUserId,
} from './utils/sessionUser'
import { getUser, getBillingStatus, updateUser } from './api/user'
import { getBrowserTimeZone } from './utils/timezone'

const navItems = [
  { label: 'Home', icon: '🏠', path: '/' },
  { label: 'Call Notes', icon: '☎️', path: '/call-notes' },
  { label: 'Action Items', icon: '📝', path: '/action-items' },
  { label: 'Contacts', icon: '👥', path: '/contacts' },
  { label: 'Calendar', icon: '📅', path: '/calendar' },
]

const secondaryNavItems = [
  { label: 'Catalog Connect', icon: '📦', path: '/catalog-connect' },
  { label: 'Settings', icon: '⚙️', path: '/settings' },
]

const comingSoonItems = [
  { label: 'CRM Sync', icon: '🗂️', path: '/crm-sync', disabled: true },
  { label: 'Agent Actions', icon: '🤖', path: '/agent-actions', disabled: true },
]

const DARK_MODE_STORAGE_KEY = 'appDarkModeEnabled'

function App() {
  const posthog = usePostHog()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchOpen, setSearchOpen] = useState(false)
  const [sessionEmail, setSessionEmail] = useState<string | null>(() => getSessionAuth())
  const [sessionToken, setSessionToken] = useState<string | null>(() => getSessionToken())
  const [isRestoringSession, setIsRestoringSession] = useState(() => !getSessionToken() && hasSessionMetadata())
  const [sessionUserId, setSessionUserId] = useState<string | null>(() => getSessionUserId())
  const [sessionVersion, setSessionVersion] = useState(0)
  const [userRefreshTick, setUserRefreshTick] = useState(0)
  const [userName, setUserName] = useState<{ first: string; last: string }>({ first: '', last: '' })
  const [userCheckStatus, setUserCheckStatus] = useState<
    'unknown' | 'checking' | 'missing' | 'exists'
  >(() => {
    if (getSessionToken()) return 'checking'
    return 'unknown'
  })
  const [billingCheckStatus, setBillingCheckStatus] = useState<BillingCheckStatus>('unknown')
  const assistantButtonRef = useRef<HTMLButtonElement | null>(null)
  const [showTopbar, setShowTopbar] = useState(true)
  const lastScrollYRef = useRef(0)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [showBugReport, setShowBugReport] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const applyThemeFromStorage = () => {
      const darkModeOn = window.localStorage.getItem(DARK_MODE_STORAGE_KEY) === 'true'
      if (darkModeOn) {
        document.documentElement.setAttribute('data-theme', 'dark')
      } else {
        document.documentElement.removeAttribute('data-theme')
      }
    }

    applyThemeFromStorage()
    window.addEventListener('storage', applyThemeFromStorage)
    window.addEventListener('focus', applyThemeFromStorage)
    document.addEventListener('visibilitychange', applyThemeFromStorage)
    return () => {
      window.removeEventListener('storage', applyThemeFromStorage)
      window.removeEventListener('focus', applyThemeFromStorage)
      document.removeEventListener('visibilitychange', applyThemeFromStorage)
    }
  }, [])

  useEffect(() => {
    if (sessionToken || !isRestoringSession) return

    let cancelled = false
    const restoreSession = async () => {
      const token = await refreshAccessToken()
      if (cancelled) return
      if (token) {
        setSessionToken(token)
        setSessionEmail(getSessionAuth())
        setUserCheckStatus('checking')
        setUserRefreshTick((prev) => prev + 1)
        setSessionVersion((prev) => prev + 1)
      }
      setIsRestoringSession(false)
    }

    void restoreSession()
    return () => {
      cancelled = true
    }
  }, [isRestoringSession, sessionToken])

  useEffect(() => {
    if (!sessionToken) {
      setUserCheckStatus('unknown')
      setUserName({ first: '', last: '' })
      return
    }

    let cancelled = false
    const controller = new AbortController()

    const loadUser = async () => {
      setUserCheckStatus('checking')
      try {
        const email = sessionEmail ?? getSessionAuth()
        const ensuredId = sessionUserId || (await ensureSessionUserId(email))
        if (!ensuredId) {
          if (!cancelled) {
            setUserCheckStatus('missing')
            setUserName({ first: '', last: '' })
          }
          return
        }
        if (!sessionUserId) setSessionUserId(ensuredId)
        let data = null
        try {
          data = await getUser(ensuredId, controller.signal)
        } catch (err) {
          const status = (err as { status?: number }).status
          if (status === 404) {
            if (!cancelled) {
              setUserCheckStatus('missing')
              setUserName({ first: '', last: '' })
            }
            return
          }
          throw err
        }

        if (!cancelled && data) {
          setUserName({
            first: (data.first_name as string) || '',
            last: (data.last_name as string) || '',
          })
          if (data.email) {
            setSessionEmail((prev) => prev ?? (data.email as string))
          }
          // If the account has no stored timezone, detect it from the browser
          // and persist it now. Without a stored tz the assistant resolves
          // user_tz to None and renders all calendar/times in UTC. Doing this at
          // session start self-heals every active user (not just those who open
          // Settings) and uses the user's real zone (no guessed default).
          if (!data.timezone) {
            const browserTz = getBrowserTimeZone()
            if (browserTz) {
              try {
                await updateUser({
                  id: (data.id as string) || ensuredId,
                  email: (data.email as string) || '',
                  cell: (data.cell as string) || '',
                  first_name: (data.first_name as string) || '',
                  last_name: (data.last_name as string) || '',
                  timezone: browserTz,
                })
              } catch (tzErr) {
                console.error('Failed to persist detected timezone', tzErr)
              }
            }
          }
          // Re-identify to PostHog on page reload so feature flags evaluate correctly
          if (ensuredId) {
            posthog?.identify(ensuredId, {
              email: (data.email as string) ?? sessionEmail ?? undefined,
              first_name: (data.first_name as string) || undefined,
              last_name: (data.last_name as string) || undefined,
            })
          }
          setUserCheckStatus('exists')
        } else if (!cancelled) {
          setUserCheckStatus('missing')
          setUserName({ first: '', last: '' })
        }
      } catch (err) {
        if (!cancelled && !controller.signal.aborted) {
          console.error('Failed to load user', err)
          setUserCheckStatus('unknown')
          setUserName({ first: '', last: '' })
        }
      }
    }

    loadUser()
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [sessionToken, userRefreshTick, sessionEmail, sessionUserId])

  useEffect(() => {
    if (userCheckStatus !== 'exists' || !sessionUserId) {
      if (!sessionToken) setBillingCheckStatus('unknown')
      return
    }

    if (import.meta.env.VITE_LOCAL_DEV_MODE === 'true') {
      setBillingCheckStatus('active')
      return
    }

    let cancelled = false
    setBillingCheckStatus('checking')

    const checkBilling = async () => {
      try {
        const data = await getBillingStatus(sessionUserId)
        if (!cancelled) {
          const isActive =
            data.billingExempt === true ||
            data.subscriptionStatus === 'active' ||
            data.subscriptionStatus === 'trialing'
          setBillingCheckStatus(isActive ? 'active' : 'inactive')
        }
      } catch {
        // Fail open — don't block users if billing check errors
        if (!cancelled) setBillingCheckStatus('active')
      }
    }

    void checkBilling()
    return () => {
      cancelled = true
    }
  }, [userCheckStatus, sessionUserId, sessionToken])

  useEffect(() => {
    if (!sessionToken) return
    if (userCheckStatus === 'missing' && location.pathname !== '/signup') {
      navigate('/signup', { replace: true })
    } else if (userCheckStatus === 'exists' && location.pathname === '/signup') {
      navigate('/', { replace: true })
    }
  }, [location.pathname, navigate, sessionToken, userCheckStatus])

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY
      const lastY = lastScrollYRef.current
      const goingDown = currentY > lastY
      lastScrollYRef.current = currentY

      if (currentY < 60) {
        setShowTopbar(true)
        return
      }

      if (goingDown && currentY - lastY > 4) {
        setShowTopbar(false)
      } else if (!goingDown && lastY - currentY > 4) {
        setShowTopbar(true)
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setMobileNavOpen(false)
  }, [location.pathname])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1100 && mobileNavOpen) {
        setMobileNavOpen(false)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [mobileNavOpen])

  const handleLogout = () => {
    posthog?.capture('user_logged_out')
    posthog?.reset()
    logoutSession()
    clearSessionUserId()
    setSessionEmail(null)
    setSessionToken(null)
    setSessionUserId(null)
    setUserName({ first: '', last: '' })
    setUserCheckStatus('unknown')
    setBillingCheckStatus('unknown')
    setSessionVersion((prev) => prev + 1)
  }

  const handleBillingActive = useCallback(() => {
    setBillingCheckStatus('active')
  }, [])

  const handleNavigateSettings = () => {
    navigate('/settings')
  }


  const handleSignupComplete = async () => {
    if (!sessionToken) return
    const ensuredId = sessionUserId || (await ensureSessionUserId(sessionEmail ?? getSessionAuth()))
    if (ensuredId) {
      setSessionUserId(ensuredId)
      setUserCheckStatus('exists')
      setUserRefreshTick((prev) => prev + 1)
      setSessionVersion((prev) => prev + 1)
      navigate('/', { replace: true })
    } else {
      setUserCheckStatus('missing')
    }
  }

  const handleAuthResolved = useCallback(
    ({
      email,
      token,
      exp,
      userId,
      firstName,
      lastName,
      provider,
    }: {
      email: string | null
      token: string
      exp?: number
      userId: string | null
      firstName?: string
      lastName?: string
      provider?: 'google' | 'azure' | 'apple' | null
    }) => {
      setSessionAuth({ email, token, exp, provider })
      setSessionToken(token)
      setSessionEmail(email ?? getSessionAuth())
      if (userId) {
        storeSessionUserId(userId)
        setSessionUserId(userId)
        posthog?.identify(userId, {
          email: email ?? undefined,
          first_name: firstName,
          last_name: lastName,
          auth_provider: provider,
        })
      }
      if (firstName || lastName) {
        setUserName({
          first: firstName || '',
          last: lastName || '',
        })
      }
      void (async () => {
        try {
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- TODO(ENG-399): investigate if this expression is intentional
          userId || (await ensureSessionUserId(email ?? getSessionAuth()))
        } catch (err) {
          console.error('Failed to ensure user id after login', err)
        }
      })()
      setUserCheckStatus('checking')
      setUserRefreshTick((prev) => prev + 1)
      setSessionVersion((prev) => prev + 1)
    },
    [posthog],
  )

  const userInitials = (() => {
    const first = userName.first.trim()
    const last = userName.last.trim()
    if (first || last) {
      const firstInitial = first ? first[0] : ''
      const lastInitial = last ? last[0] : ''
      return `${firstInitial}${lastInitial || firstInitial}`.slice(0, 2).toUpperCase() || '??'
    }
    if (!sessionEmail) return '??'
    const namePart = sessionEmail.split('@')[0] || sessionEmail
    const segments = namePart.split(/[._\-+]/).filter(Boolean)
    if (segments.length >= 2) {
      return (segments[0][0] + segments[1][0]).toUpperCase()
    }
    return namePart.slice(0, 2).toUpperCase()
  })()

  const normalizedPath = (() => {
    if (location.pathname.startsWith('/contact/')) return '/contacts'
    if (location.pathname.startsWith('/conversation/')) return '/call-notes'
    if (location.pathname.startsWith('/archives')) return '/settings'
    if (location.pathname.startsWith('/memories')) return '/settings'
    if (location.pathname.startsWith('/payments')) return '/settings'
    return location.pathname
  })()

  const activePath =
    [...navItems, ...secondaryNavItems].find((item) =>
      normalizedPath === item.path || normalizedPath.startsWith(`${item.path}/`),
    )?.path ?? (normalizedPath === '/' ? '' : '/call-notes')

  const contentClass =
    location.pathname.startsWith('/calendar') || location.pathname.startsWith('/calendar/')
      ? 'calendarContent'
      : 'content'

  const guardRoute = (element: ReactElement) =>
    userCheckStatus === 'exists' ? (
      <ErrorBoundary>{element}</ErrorBoundary>
    ) : (
      <Navigate to="/signup" replace />
    )

  if (location.pathname === '/redirect') {
    return <RedirectView onAuthResolved={handleAuthResolved} />
  }

  if (sessionToken && userCheckStatus === 'checking') {
    return <LoginView isChecking onAuthResolved={handleAuthResolved} />
  }

  if (!sessionToken) {
    return <LoginView isChecking={isRestoringSession} onAuthResolved={handleAuthResolved} />
  }

  if (userCheckStatus !== 'exists') {
    return (
      <div className="signup-shell">
        <div className="signup-panel">
          <SignupView sessionEmail={sessionEmail} onComplete={handleSignupComplete} onLogout={handleLogout} />
        </div>
      </div>
    )
  }

  // Allow Stripe redirect pages through before the billing gate
  if (location.pathname === '/payments/success') {
    return <PaymentResultView variant="success" onBillingActive={handleBillingActive} onLogout={handleLogout} />
  }
  if (location.pathname === '/payments/cancel') {
    return <PaymentResultView variant="cancel" onLogout={handleLogout} />
  }

  if (billingCheckStatus === 'unknown' || billingCheckStatus === 'checking') {
    return <LoginView isChecking onAuthResolved={handleAuthResolved} />
  }

  if (billingCheckStatus === 'inactive') {
    return (
      <div className="signup-shell">
        <div className="paywall-panel">
          <PaymentsView onLogout={handleLogout} />
        </div>
      </div>
    )
  }

  const onboardingStatus = import.meta.env.VITE_LOCAL_DEV_MODE === 'true'
    ? 'complete'
    : localStorage.getItem(ONBOARDING_STATUS_KEY)
  if (onboardingStatus && onboardingStatus !== 'complete' && onboardingStatus !== 'skipped') {
    return <OnboardingView status={onboardingStatus} userId={sessionUserId} />
  }

  const isAssistant = location.pathname === '/assistant'

  return (
    <div className="app-shell">
      <Sidebar items={navItems} secondaryItems={secondaryNavItems} activePath={activePath} comingSoon={comingSoonItems} onReportIssue={() => setShowBugReport(true)} />
      <div
        className={`nav-backdrop ${mobileNavOpen ? 'open' : ''}`}
        onClick={() => setMobileNavOpen(false)}
      />
      <Sidebar
        items={navItems}
        secondaryItems={secondaryNavItems}
        activePath={activePath}
        comingSoon={comingSoonItems}
        variant="mobile"
        className={`sidebar-drawer ${mobileNavOpen ? 'open' : ''}`}
        onNavigate={() => setMobileNavOpen(false)}
        onClose={() => setMobileNavOpen(false)}
        onReportIssue={() => { setMobileNavOpen(false); setShowBugReport(true) }}
      />

      <main className="main">
        {onboardingStatus === 'skipped' && <OnboardingSkipBanner />}
        {onboardingStatus === 'complete' && <OnboardingCoachMark />}
        <Topbar
          assistantButtonRef={assistantButtonRef}
          userInitials={userInitials}
          onSettings={handleNavigateSettings}
          onLogout={handleLogout}
          hidden={!showTopbar}
          onToggleMenu={() => setMobileNavOpen((prev) => !prev)}
          mobileNavOpen={mobileNavOpen}
          searchOpen={searchOpen}
          setSearchOpen={setSearchOpen}
        />

        <section className={`${contentClass} ${isAssistant ? 'assistant-view-active' : ''}`} key={sessionVersion}>
          <Suspense fallback={<LoadingPlaceholder variant="all" />}>
            <Routes>
              <Route path="/" element={guardRoute(<HomeView firstName={userName.first} />)} />
              <Route
                path="/call-notes"
                element={guardRoute(<DashboardView />)}
              />
              <Route
                path="/contacts"
                element={guardRoute(<ContactsView />)}
              />
              <Route
                path="/contacts/merge"
                element={guardRoute(<ContactMergeView />)}
              />
              <Route
                path="/assistant"
                element={guardRoute(<AssistantView />)}
              />
              <Route
                path="/contact/new"
                element={guardRoute(<ContactCreateView />)}
              />
              <Route
                path="/contact/:id"
                element={guardRoute(<ContactDetailView />)}
              />
              <Route
                path="/catalog-connect"
                element={guardRoute(<CatalogUploaderView />)}
              />
              <Route
                path="/catalog"
                element={guardRoute(<CatalogRawView />)}
              />
              <Route
                path="/payments"
                element={guardRoute(<PaymentsView variant="billing" />)}
              />
              <Route
                path="/payments/success"
                element={guardRoute(<PaymentResultView variant="success" />)}
              />
              <Route
                path="/payments/cancel"
                element={guardRoute(<PaymentResultView variant="cancel" />)}
              />
              <Route
                path="/calendar"
                element={guardRoute(<CalendarView />)}
              />
              <Route
                path="/action-items"
                element={guardRoute(<ActionItemsView />)}
              />
              <Route path="/crm-sync" element={guardRoute(<CrmSyncView />)} />
              <Route path="/settings" element={guardRoute(<SettingsView />)} />
              <Route path="/settings/flags" element={guardRoute(<FeatureFlagsView />)} />
              <Route path="/archives" element={guardRoute(<ArchivesView />)} />
              <Route
                path="/conversation/:id"
                element={guardRoute(<ConversationDetailView />)}
              />
              <Route path='/memories' element={guardRoute(<MemoriesView />)}/>
              <Route
                path="/signup"
                element={<ErrorBoundary><SignupView sessionEmail={sessionEmail} onComplete={handleSignupComplete} onLogout={handleLogout} /></ErrorBoundary>}
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </section>
      </main>

      <CommandPalette />
      {showBugReport && (
        <BugReportModal
          onClose={() => setShowBugReport(false)}
          userId={sessionUserId}
          userEmail={sessionEmail}
        />
      )}
    </div>
  )
}

export default App
